import React, { useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp, Landmark } from 'lucide-react';
import { useDashboard } from '../../DashboardContext';
import { S } from '../../lib/constants';
import Input from '../shared/Input';

const label11 = { fontSize: 11, color: '#525252', textTransform: 'uppercase', letterSpacing: '0.05em' };
const mono = (color = '#1A1A1A', size = 14) => ({ fontFamily: "'IBM Plex Mono', monospace", fontVariantNumeric: 'tabular-nums', color, fontSize: size });

export default function StrategyBuilderSection() {
  const {
    activeStrategy,
    activeCalc,
    timeHorizon,
    updateStrategy,
    updateStrategyExpansion,
    fmt,
  } = useDashboard();

  const [openExpansion, setOpenExpansion] = useState(null); // original index of the card being edited

  const strat = activeStrategy;
  const fleetCapEx = strat.startingMiners * strat.minerPrice;
  const fleetPowerKW = (strat.startingMiners * strat.powerDraw) / 1000;

  // Display expansions in chronological order but mutate by original index
  const orderedExpansions = strat.expansions
    .map((e, i) => ({ e, i }))
    .sort((a, b) => a.e.month - b.e.month);

  const addExpansion = () => {
    const lastMonth = strat.expansions.length ? Math.max(...strat.expansions.map(e => e.month)) + 12 : 12;
    const newExpansion = { month: lastMonth, miners: 5, hashRate: 318, powerDraw: 3498, minerPrice: 6000, lifespan: 60, resaleValue: 600, opexCoverage: strat.initialOpexCoverage || 'otherIncome', useLoan: false, loanAmount: 0, loanRate: 0.08, loanTerm: 36, interestOnly: true };
    updateStrategy({ expansions: [...strat.expansions, newExpansion] });
    setOpenExpansion(strat.expansions.length); // open the new one for editing
  };

  const removeExpansion = (idx) => {
    updateStrategy({ expansions: strat.expansions.filter((_, x) => x !== idx) });
    setOpenExpansion(null);
  };

  return (
    <div className="space-y-6">
      {/* ── This plan's configuration ── */}
      <div style={{ ...S.card }} className="p-5">
        <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
          <div className="min-w-0">
            <h3 className="font-semibold text-lg flex items-center gap-2" style={{ color: '#1A1A1A' }}>
              <Landmark className="w-5 h-5" style={{ color: '#92700C' }} /> Plan configuration
            </h3>
            <p className="text-xs mt-1" style={{ color: '#737373', maxWidth: '62ch' }}>
              A plan holds one configuration. To weigh a different approach, save it as its own plan and
              line the two up in Compare.
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <div style={label11}>Starting capital</div>
            <div style={mono('#1A1A1A', 20)}>{fmt(fleetCapEx)}</div>
          </div>
        </div>

        {/* Fleet + funding + policy in three calm columns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          {/* Starting fleet */}
          <div className="p-4" style={{ ...S.innerCard, borderRadius: 8 }}>
            <div className="flex items-baseline justify-between mb-3">
              <span style={label11}>Starting fleet</span>
              <span style={mono('#1A1A1A', 18)}>{fmt(fleetCapEx)}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <Input label="Miners" value={strat.startingMiners} onChange={(v) => updateStrategy({ startingMiners: v })} min={1} max={10000} />
              <Input label="$ / miner" value={strat.minerPrice} onChange={(v) => updateStrategy({ minerPrice: v })} min={100} max={30000} />
              <Input label="TH/s each" value={strat.hashRate} onChange={(v) => updateStrategy({ hashRate: v })} min={1} max={1000} />
              <Input label="Watts each" value={strat.powerDraw} onChange={(v) => updateStrategy({ powerDraw: v })} min={100} max={10000} />
              <Input label="Lifespan (mo)" value={strat.initialMinerLifespan} onChange={(v) => updateStrategy({ initialMinerLifespan: v })} min={12} max={120} />
              <Input label="Resale $" value={strat.initialResaleValue} onChange={(v) => updateStrategy({ initialResaleValue: v })} min={0} max={5000} />
            </div>
            <div className="flex items-center justify-between text-xs" style={{ color: '#737373' }}>
              <span>{fleetPowerKW.toFixed(1)} kW total draw</span>
              <label className="flex items-center gap-1.5" style={{ color: '#333' }}>
                <input type="checkbox" checked={strat.initialCapexIsSunk} onChange={(e) => updateStrategy({ initialCapexIsSunk: e.target.checked })} className="rounded accent-[#92700C]" />
                Already owned
              </label>
            </div>
          </div>

          {/* Funding */}
          <div className="p-4" style={{ ...S.innerCard, borderRadius: 8 }}>
            <div className="flex items-baseline justify-between mb-3">
              <span style={label11}>Funding</span>
              {strat.useInitialFinancing && <span style={mono('#15803D', 14)}>+{fmt(strat.initialLoanAmount)} loan</span>}
            </div>
            <label className="flex items-center gap-2 text-sm mb-3" style={{ color: '#333' }}>
              <input type="checkbox" checked={strat.useInitialFinancing} onChange={(e) => updateStrategy({ useInitialFinancing: e.target.checked })} className="rounded accent-[#7C3AED]" />
              Finance with a loan
            </label>
            {strat.useInitialFinancing && (
              <div className="grid grid-cols-2 gap-2">
                <Input label="Amount $" value={strat.initialLoanAmount} onChange={(v) => updateStrategy({ initialLoanAmount: v })} min={0} max={10000000} step={5000} />
                <Input label="Rate %" value={strat.initialLoanRate * 100} onChange={(v) => updateStrategy({ initialLoanRate: v / 100 })} min={0} max={30} step={0.5} />
                <Input label="Term (mo)" value={strat.initialLoanTerm} onChange={(v) => updateStrategy({ initialLoanTerm: v })} min={6} max={120} />
                <label className="flex items-center gap-1.5 text-xs self-end pb-2.5" style={{ color: '#333' }}>
                  <input type="checkbox" checked={strat.initialInterestOnly} onChange={(e) => updateStrategy({ initialInterestOnly: e.target.checked })} className="rounded accent-[#7C3AED]" />
                  Interest only
                </label>
              </div>
            )}
          </div>

          {/* Funding & policy */}
          <div className="p-4" style={{ ...S.innerCard, borderRadius: 8 }}>
            <div className="mb-3 flex items-baseline justify-between">
              <span style={label11}>Funding & policy</span>
              {(strat.fundingEndMonth || 9999) < timeHorizon * 12 && (
                <span className="text-xs px-2 py-0.5 rounded" style={{ background: '#F0FDF4', color: '#15803D' }}>then self-sustaining</span>
              )}
            </div>
            <div className="mb-2">
              <span style={{ ...label11, display: 'block', marginBottom: 6 }}>Bills paid with</span>
              <select value={strat.initialOpexCoverage || 'btc'} onChange={(e) => updateStrategy({ initialOpexCoverage: e.target.value })}
                className="w-full px-3 py-2 text-sm"
                style={{ background: '#FFF', border: '1px solid #D4D4D4', color: '#1A1A1A', borderRadius: 4, cursor: 'pointer' }}>
                <option value="otherIncome">Outside funding (keep all BTC)</option>
                <option value="btc">Sold BTC production</option>
              </select>
            </div>
            <div className="mb-2">
              <span style={{ ...label11, display: 'block', marginBottom: 6 }}>Outside funding</span>
              <select value={strat.fundingMode || 'fixed'} onChange={(e) => updateStrategy({ fundingMode: e.target.value })}
                className="w-full px-3 py-2 text-sm"
                style={{ background: '#FFF', border: '1px solid #D4D4D4', color: '#1A1A1A', borderRadius: 4, cursor: 'pointer' }}>
                <option value="fixed">Fixed monthly contribution</option>
                <option value="shortfall">Cover bills as needed</option>
              </select>
              <div className="text-xs mt-1" style={{ color: '#737373' }}>
                {(strat.fundingMode || 'fixed') === 'fixed' ? 'Full amount deposited monthly; surplus builds cash' : 'Draws only what the bills need, up to the cap'}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <Input label={(strat.fundingMode || 'fixed') === 'fixed' ? 'Contribution $/mo' : 'Draw cap $/mo'} value={strat.otherIncome || 0} onChange={(v) => updateStrategy({ otherIncome: v })} min={0} max={1000000} step={100} />
              <Input label="Extra BTC sold %" value={(strat.sellPercent || 0) * 100} onChange={(v) => updateStrategy({ sellPercent: v / 100 })} min={0} max={100} step={1} />
              <Input label="Fund from yr" value={Math.max(1, Math.ceil((strat.fundingStartMonth || 1) / 12))} onChange={(v) => updateStrategy({ fundingStartMonth: Math.max(1, (v - 1) * 12 + 1) })} min={1} max={21} />
              <Input label="Through yr" value={(strat.fundingEndMonth || 9999) >= 9999 ? timeHorizon : Math.ceil(strat.fundingEndMonth / 12)} onChange={(v) => updateStrategy({ fundingEndMonth: v >= timeHorizon ? 9999 : v * 12 })} min={1} max={21} />
            </div>
            <label className="flex items-center gap-1.5 text-xs" style={{ color: '#333' }}>
              <input type="checkbox" checked={strat.autoSellForCashBuffer || false} onChange={(e) => updateStrategy({ autoSellForCashBuffer: e.target.checked })} className="rounded accent-[#0E7490]" />
              Sell BTC to keep cash above
            </label>
            {strat.autoSellForCashBuffer && (
              <div className="mt-2">
                <Input label="Cash floor $" value={strat.cashBufferMin || 5000} onChange={(v) => updateStrategy({ cashBufferMin: v })} min={0} max={100000} step={1000} />
              </div>
            )}
          </div>
        </div>

        {/* ── Growth timeline ── */}
        <div>
          <div className="flex items-baseline justify-between mb-4">
            <span style={label11}>Growth timeline</span>
            <span className="text-xs" style={{ color: '#737373' }}>
              {orderedExpansions.length ? `${orderedExpansions.length} expansion${orderedExpansions.length > 1 ? 's' : ''} · ${fmt(strat.expansions.reduce((s, e) => s + e.miners * e.minerPrice, 0))} planned` : 'No expansions yet'}
            </span>
          </div>

          <div style={{ position: 'relative', paddingLeft: 28 }}>
            {/* Spine */}
            <div style={{ position: 'absolute', left: 9, top: 6, bottom: 6, width: 2, background: '#E5E5E5' }} />

            {/* Month 1: starting fleet marker */}
            <div style={{ position: 'relative', marginBottom: 14 }}>
              <div style={{ position: 'absolute', left: -25, top: 8, width: 12, height: 12, borderRadius: '50%', background: strat.color, border: '2px solid #FFF', boxSizing: 'border-box' }} />
              <div className="flex items-baseline gap-3 py-1.5">
                <span style={mono('#525252', 12)}>Mo 1</span>
                <span className="text-sm" style={{ color: '#1A1A1A', fontWeight: 500 }}>{strat.startingMiners} miners online</span>
                <span style={mono('#737373', 12)}>{strat.initialCapexIsSunk ? 'already owned' : fmt(fleetCapEx)}</span>
              </div>
            </div>

            {/* Expansion cards */}
            {orderedExpansions.map(({ e, i }) => {
              const open = openExpansion === i;
              const capex = e.miners * e.minerPrice;
              return (
                <div key={i} style={{ position: 'relative', marginBottom: 10 }}>
                  <div style={{ position: 'absolute', left: -25, top: 16, width: 12, height: 12, borderRadius: '50%', background: '#FFF', border: `3px solid ${e.miners > 0 ? '#92700C' : '#D4D4D4'}`, boxSizing: 'border-box' }} />
                  <div style={{ background: '#FFF', border: open ? '1px solid #92700C' : '1px solid #D4D4D4', borderRadius: 8, overflow: 'hidden' }}>
                    {/* Summary row — the only thing visible until you click */}
                    <button onClick={() => setOpenExpansion(open ? null : i)}
                      className="w-full flex items-center gap-4 px-4 py-3 text-left"
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
                      <span style={mono('#525252', 12)}>Mo {e.month}</span>
                      <span className="text-sm font-medium" style={{ color: '#1A1A1A' }}>+{e.miners} miners</span>
                      <span style={mono('#737373', 12)}>{fmt(capex)}</span>
                      {e.useLoan && e.loanAmount > 0 && (
                        <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded" style={{ background: '#F5F3FF', color: '#7C3AED' }}>
                          <Landmark className="w-3 h-3" /> {fmt(e.loanAmount)} financed
                        </span>
                      )}
                      <span style={{ flex: 1 }} />
                      <span className="text-xs" style={{ color: '#737373' }}>Yr {Math.ceil(e.month / 12)}</span>
                      {open ? <ChevronUp className="w-4 h-4" style={{ color: '#737373' }} /> : <ChevronDown className="w-4 h-4" style={{ color: '#737373' }} />}
                    </button>

                    {/* Inline editor */}
                    {open && (
                      <div className="px-4 pb-4 pt-1" style={{ borderTop: '1px solid #F5F5F5' }}>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                          <Input label="Month" value={e.month} onChange={(v) => updateStrategyExpansion(i, 'month', v)} min={1} max={240} />
                          <Input label="Miners" value={e.miners} onChange={(v) => updateStrategyExpansion(i, 'miners', v)} min={0} max={1000} />
                          <Input label="$ / miner" value={e.minerPrice} onChange={(v) => updateStrategyExpansion(i, 'minerPrice', v)} min={100} max={50000} />
                          <Input label="TH/s each" value={e.hashRate} onChange={(v) => updateStrategyExpansion(i, 'hashRate', v)} min={1} max={1000} />
                          <Input label="Watts each" value={e.powerDraw} onChange={(v) => updateStrategyExpansion(i, 'powerDraw', v)} min={100} max={10000} />
                          <Input label="Lifespan (mo)" value={e.lifespan} onChange={(v) => updateStrategyExpansion(i, 'lifespan', v)} min={12} max={120} />
                          <Input label="Resale $" value={e.resaleValue} onChange={(v) => updateStrategyExpansion(i, 'resaleValue', v)} min={0} max={5000} />
                        </div>
                        <div className="flex items-center gap-4 flex-wrap">
                          <label className="flex items-center gap-1.5 text-sm" style={{ color: '#333' }}>
                            <input type="checkbox" checked={e.useLoan} onChange={(x) => updateStrategyExpansion(i, 'useLoan', x.target.checked)} className="rounded accent-[#7C3AED]" />
                            Finance with a loan
                          </label>
                          {e.useLoan && (
                            <div className="grid grid-cols-3 gap-2 flex-1" style={{ maxWidth: 360 }}>
                              <Input label="Amount $" value={e.loanAmount} onChange={(v) => updateStrategyExpansion(i, 'loanAmount', v)} min={0} max={10000000} step={5000} />
                              <Input label="Rate %" value={e.loanRate * 100} onChange={(v) => updateStrategyExpansion(i, 'loanRate', v / 100)} min={0} max={30} step={0.5} />
                              <Input label="Term (mo)" value={e.loanTerm} onChange={(v) => updateStrategyExpansion(i, 'loanTerm', v)} min={6} max={120} />
                            </div>
                          )}
                          {e.useLoan && (
                            <label className="flex items-center gap-1.5 text-xs" style={{ color: '#525252' }}>
                              <input type="checkbox" checked={e.interestOnly} onChange={(x) => updateStrategyExpansion(i, 'interestOnly', x.target.checked)} className="rounded accent-[#7C3AED]" />
                              Interest only
                            </label>
                          )}
                          <span style={{ flex: 1 }} />
                          <button onClick={() => removeExpansion(i)}
                            className="flex items-center gap-1 text-xs px-2 py-1.5 rounded"
                            style={{ background: 'transparent', color: '#DC2626', border: '1px solid #FECACA', cursor: 'pointer' }}>
                            <Trash2 className="w-3 h-3" /> Remove
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Fleet retirement warning — only when miners die before the horizon */}
            {activeCalc?.fleetOfflineMonth > 0 && activeCalc.fleetOfflineMonth < timeHorizon * 12 && (
              <div style={{ position: 'relative', marginBottom: 10 }}>
                <div style={{ position: 'absolute', left: -25, top: 14, width: 12, height: 12, borderRadius: '50%', background: '#FEF9EC', border: '2px solid #92700C', boxSizing: 'border-box' }} />
                <div className="flex items-baseline gap-3 px-4 py-3 rounded" style={{ background: '#FEF9EC', border: '1px solid #E7D9AE' }}>
                  <span style={mono('#92700C', 12)}>Mo {activeCalc.fleetOfflineMonth}</span>
                  <span className="text-sm" style={{ color: '#1A1A1A', fontWeight: 500 }}>Last miners retire</span>
                  <span className="text-xs" style={{ color: '#525252' }}>
                    The final {Math.max(1, timeHorizon - Math.ceil(activeCalc.fleetOfflineMonth / 12))} year{timeHorizon - Math.ceil(activeCalc.fleetOfflineMonth / 12) > 1 ? 's' : ''} of this plan produce nothing. Add an expansion to keep earning.
                  </span>
                </div>
              </div>
            )}

            {/* Ghost add card */}
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', left: -25, top: 12, width: 12, height: 12, borderRadius: '50%', background: '#FFF', border: '2px dashed #B9B9B9', boxSizing: 'border-box' }} />
              <button onClick={addExpansion}
                className="w-full flex items-center gap-2 px-4 py-3 text-sm rounded"
                style={{ background: 'transparent', border: '1px dashed #B9B9B9', color: '#737373', cursor: 'pointer' }}>
                <Plus className="w-4 h-4" /> Add expansion
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
